import { useState, useEffect } from "react";
import { apiFetch } from '../lib/apiFetch';
import { useLanguage } from "./LanguageContext";

const css = `
  .run-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
  }
  .run-modal {
    background: white;
    border-radius: 14px;
    width: 1100px;
    max-width: 95vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.3);
    overflow: hidden;
    font-family: 'new-science', 'DM Sans', sans-serif;
  }
  .run-header {
    padding: 18px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .run-title { font-size: 17px; font-weight: 700; color: #0f172a; }
  .run-subtitle { font-size: 12.5px; color: #64748b; margin-top: 2px; }
  .run-header-right { display: flex; align-items: center; gap: 10px; }
  .run-close {
    background: none; border: none; font-size: 22px; color: #94a3b8;
    cursor: pointer; padding: 0; width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 6px; transition: background 0.15s;
  }
  .run-close:hover { background: #f1f5f9; color: #475569; }
  .run-select {
    padding: 6px 10px; border-radius: 7px; border: 1.5px solid #e2e8f0;
    font-family: 'new-science', 'DM Sans', sans-serif; font-size: 12.5px;
    color: #334155; background: white; cursor: pointer; min-width: 220px;
  }
  .run-select:focus { outline: none; border-color: #2563eb; }
  .run-body { flex: 1; overflow-y: auto; padding: 20px 24px; }
  .run-footer {
    padding: 14px 24px; border-top: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    background: #f8fafc;
  }
  .run-btn {
    padding: 8px 16px; border-radius: 7px;
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: all 0.15s; border: none;
    display: flex; align-items: center; gap: 7px;
  }
  .run-btn-ghost { background: white; border: 1.5px solid #e2e8f0; color: #64748b; }
  .run-btn-ghost:hover { border-color: #94a3b8; color: #0f172a; }
  .run-btn-primary { background: #2563eb; color: white; box-shadow: 0 2px 8px rgba(37,99,235,0.25); }
  .run-btn-primary:hover:not(:disabled) { background: #1d4ed8; }
  .run-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .run-term {
    background: #f8fafc; border: 1px solid #e2e8f0;
    border-radius: 10px; padding: 16px; margin-bottom: 14px;
  }
  .run-term-q {
    font-size: 14px; font-weight: 600; color: #0f172a;
    margin-bottom: 12px; display: flex; align-items: flex-start;
    gap: 8px; line-height: 1.4;
  }
  .run-engines { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .run-engine { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
  .run-engine-name {
    font-size: 11px; font-weight: 600; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.06em;
    margin-bottom: 8px; display: flex; align-items: center; gap: 5px;
  }
  .run-engine-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .run-engine-response {
    font-size: 12.5px; color: #334155; line-height: 1.55;
    max-height: 130px; overflow-y: auto; white-space: pre-wrap;
  }
  .run-engine-response.error { color: #dc2626; }
  .run-engine-response.empty { color: #94a3b8; font-style: italic; }
  .run-state { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 48px 20px; color: #94a3b8; }
  .run-state-icon { font-size: 36px; margin-bottom: 12px; }
  .run-state-title { font-size: 15px; font-weight: 600; color: #64748b; margin-bottom: 6px; }
  .run-state-sub { font-size: 13px; line-height: 1.5; }
  .run-spinner {
    width: 16px; height: 16px;
    border: 2.5px solid rgba(255,255,255,0.4); border-top-color: white;
    border-radius: 50%; animation: runSpin 0.8s linear infinite;
  }
  .run-spinner-lg {
    width: 32px; height: 32px; border: 3px solid #e2e8f0;
    border-top-color: #2563eb; border-radius: 50%;
    animation: runSpin 0.8s linear infinite; margin: 0 auto 16px;
  }
  @keyframes runSpin { to { transform: rotate(360deg); } }
  .run-badge {
    display: inline-flex; align-items: center;
    padding: 2px 8px; border-radius: 100px; font-size: 11px;
    font-weight: 600; background: #dcfce7; color: #16a34a;
    border: 1px solid #bbf7d0; margin-left: 8px;
  }
  /* Topic checkboxes in ready state */
  .run-topic-list {
    display: flex; flex-direction: column; gap: 8px;
    margin: 16px 0 0;
    max-width: 480px;
    margin-left: auto; margin-right: auto;
  }
  .run-topic-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; border-radius: 8px;
    border: 1.5px solid #e2e8f0; background: white;
    cursor: pointer; transition: border-color 0.15s, background 0.15s;
    text-align: left;
    font-family: 'new-science', 'DM Sans', sans-serif;
    width: 100%;
  }
  .run-topic-item:hover { border-color: #94a3b8; background: #f8fafc; }
  .run-topic-item.checked { border-color: #2563eb; background: #eff6ff; }
  .run-topic-check {
    width: 16px; height: 16px; border-radius: 4px;
    border: 1.5px solid #cbd5e1; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: transparent; transition: all 0.15s;
  }
  .run-topic-item.checked .run-topic-check {
    background: #2563eb; border-color: #2563eb; color: white;
  }
  .run-topic-name { font-size: 13.5px; color: #0f172a; flex: 1; }
  .run-topic-item.checked .run-topic-name { color: #1d4ed8; font-weight: 500; }
  .run-topic-count { font-size: 11px; color: #94a3b8; }
  .run-select-all {
    font-size: 12px; color: #2563eb; background: none; border: none;
    cursor: pointer; font-family: inherit; padding: 0;
    text-decoration: underline; text-underline-offset: 2px;
  }
`;

const ENGINES = {
  openai:     { name: "ChatGPT",    color: "#10a37f" },
  claude:     { name: "Claude",     color: "#cc785c" },
  gemini:     { name: "Gemini",     color: "#4285f4" },
  perplexity: { name: "Perplexity", color: "#6366f1" },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-CH", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function ResultsView({ results, t }) {
  if (!results?.length) return (
    <div className="run-state">
      <div className="run-state-icon">📭</div>
      <div className="run-state-title">{t("run.noResults")}</div>
    </div>
  );
  return results.map((result, i) => (
    <div key={i} className="run-term">
      <div className="run-term-q">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" style={{flexShrink:0,marginTop:2}}>
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
        {result.term}
      </div>
      <div className="run-engines">
        {Object.entries(ENGINES).map(([key, engine]) => {
          const response = result.responses?.[key];
          return (
            <div key={key} className="run-engine">
              <div className="run-engine-name">
                <span className="run-engine-dot" style={{background: engine.color}} />
                {engine.name}
              </div>
              <div className={`run-engine-response ${!response ? "empty" : response.startsWith("Error") ? "error" : ""}`}>
                {response || t("run.noResponse")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ));
}

// mode="refresh"  → zeigt Ready-Screen mit Topic-Auswahl, dann Run
// mode="data"     → zeigt vergangene Runs
// mode="multi"    → wird von Dashboard nach Wizard aufgerufen, allTopics + allTermsByTopic übergeben
export default function TopicRunModal({ topic, terms, brand, mode = "refresh", onClose, onRunComplete, allTopics = null, allTermsByTopic = null }) {
  const { t } = useLanguage();
  const isDataMode = mode === "data";
  const isMultiMode = mode === "multi";

  // Topics for multi-mode: all topics with their terms
  const topicsForSelection = isMultiMode && allTopics
    ? allTopics
    : topic ? [topic] : [];

  const [selectedTopicIds, setSelectedTopicIds] = useState(
    () => topicsForSelection.map(tp => tp.id)
  );

  const [running, setRunning]     = useState(false);
  const [runResults, setRunResults] = useState(null);
  const [runSaved, setRunSaved]   = useState(false);
  const [pastRuns, setPastRuns]   = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [evalDone, setEvalDone]     = useState(false);

  const handleEvaluate = async () => {
    if (!selectedRun || !brand?.id) return;
    setEvaluating(true);
    setEvalDone(false);
    try {
      const runIds = selectedRun.termResults.map(t => t.runId).filter(Boolean);
      const res = await apiFetch("/evaluate-run", {
        method: "POST",
        body: JSON.stringify({ brandId: brand.id, runIds }),
      });
      const data = await res.json();
      setEvalDone(data.evaluated > 0);
    } catch (e) {
      console.error("Eval error:", e);
    } finally {
      setEvaluating(false);
    }
  };

  useEffect(() => {
    if (isDataMode) loadPastRuns();
  }, [isDataMode, topic?.id]);

  const loadPastRuns = async () => {
    setLoadingRuns(true);
    try {
      const res = await apiFetch(`/runs?topicId=${topic.id}`);
      const data = await res.json();
      setPastRuns(data.runs || []);
      if (data.runs?.length > 0) setSelectedRun(data.runs[0]);
    } catch (err) {
      console.error("Error loading runs:", err);
    } finally {
      setLoadingRuns(false);
    }
  };

  const toggleTopic = (id) => {
    setSelectedTopicIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getTermsForRun = () => {
    if (isMultiMode && allTermsByTopic) {
      // Collect terms for all selected topics
      const result = [];
      selectedTopicIds.forEach(topicId => {
        const topicTerms = allTermsByTopic[topicId] || [];
        topicTerms.forEach(t => result.push({ id: t.id, term: t.term }));
      });
      return result;
    }
    return terms.map(t => ({ id: t.id, term: t.term }));
  };

  const getTopicIdForRun = () => {
    // For single-topic runs, use the topic id directly
    // For multi-topic, use the first selected topic (backend handles per topic)
    if (isMultiMode && selectedTopicIds.length > 0) return selectedTopicIds[0];
    return topic?.id;
  };

  const handleRun = async () => {
    setRunning(true);
    setRunResults(null);
    setRunSaved(false);
    try {
      if (isMultiMode) {
        // Multi-mode: run separately per topic so topicId matches search_term_ids
        let allResults = [];
        let totalSaved = 0;
        for (const topicId of selectedTopicIds) {
          const topicTerms = (allTermsByTopic?.[topicId] || []).map(t => ({ id: t.id, term: t.term }));
          if (topicTerms.length === 0) continue;
          const response = await apiFetch("/run", {
            method: "POST",
            body: JSON.stringify({
              topicId,
              brandId: brand.id,
              country: brand.default_country || "Switzerland",
              language: brand.default_language || "German",
              terms: topicTerms,
            }),
          });
          if (!response.ok) throw new Error(`Server error: ${response.status}`);
          const data = await response.json();
          allResults = [...allResults, ...(data.results || [])];
          totalSaved += data.savedCount || 0;
        }
        setRunResults(allResults);
        setRunSaved(totalSaved > 0);
        if (onRunComplete) onRunComplete();
      } else {
        // Single-topic mode
        const termsToRun = getTermsForRun();
        if (termsToRun.length === 0) {
          alert("Keine Search Terms ausgewählt.");
          setRunning(false);
          return;
        }
        const response = await apiFetch("/run", {
          method: "POST",
          body: JSON.stringify({
            topicId: topic?.id,
            brandId: brand.id,
            country: brand.default_country || "Switzerland",
            language: brand.default_language || "German",
            terms: termsToRun,
          }),
        });
        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        const data = await response.json();
        setRunResults(data.results);
        setRunSaved(data.savedCount > 0);
        if (onRunComplete) onRunComplete();
      }
    } catch (err) {
      console.error("Run error:", err);
      alert(t("run.errRunning") + err.message);
    } finally {
      setRunning(false);
    }
  };

  const totalTermsSelected = getTermsForRun().length;

  const titleText = isMultiMode
    ? `🚀 ${brand?.primary_name || ""}`
    : isDataMode
    ? `📊 ${t("dash.topicPrefix")}: ${topic?.name}`
    : `🚀 ${t("dash.topicPrefix")}: ${topic?.name}`;

  const subtitleText = isMultiMode
    ? `${topicsForSelection.length} Topics • 4 ${t("run.aiEngines")}`
    : `${terms?.length || 0} ${t("run.searchTerms")} • 4 ${t("run.aiEngines")}${isDataMode ? ` • ${pastRuns.length} ${t("run.runsAvailable")}` : ""}`;

  return (
    <div className="run-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <style>{css}</style>
      <div className="run-modal">

        {/* Header */}
        <div className="run-header">
          <div>
            <div className="run-title">
              {titleText}
              {runSaved && <span className="run-badge">✓ {t("run.saved")}</span>}
            </div>
            <div className="run-subtitle">
              {subtitleText}
              {/* Topic pill — single topic mode only */}
              {!isMultiMode && topic?.name && (
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  background: "rgba(37,99,235,0.12)", color: "#2563eb",
                  borderRadius: 100, padding: "2px 10px", fontSize: 12,
                  fontWeight: 600, marginLeft: 10,
                }}>
                  {topic.name}
                </span>
              )}
            </div>
          </div>
          <div className="run-header-right">
            {isDataMode && pastRuns.length > 0 && (
              <select
                className="run-select"
                value={selectedRun?.batchKey || ""}
                onChange={e => setSelectedRun(pastRuns.find(r => r.batchKey === e.target.value))}
              >
                {pastRuns.map((run, i) => (
                  <option key={run.batchKey} value={run.batchKey}>
                    {formatDate(run.runDate)}{i === 0 ? ` (${t("run.lastRun")})` : ""}
                  </option>
                ))}
              </select>
            )}
            <button className="run-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Body */}
        <div className="run-body">
          {isDataMode ? (
            loadingRuns ? (
              <div className="run-state">
                <div className="run-spinner-lg" />
                <div className="run-state-title">{t("run.loadingData")}</div>
              </div>
            ) : pastRuns.length === 0 ? (
              <div className="run-state">
                <div className="run-state-icon">📭</div>
                <div className="run-state-title">{t("run.noData")}</div>
                <div className="run-state-sub">{t("run.noDataSub")}</div>
              </div>
            ) : (
              <ResultsView results={selectedRun?.termResults} t={t} />
            )
          ) : (
            !runResults && !running ? (
              <div className="run-state">
                <div className="run-state-icon">🚀</div>
                <div className="run-state-title">{t("run.readyTitle")}</div>
                <div className="run-state-sub" style={{marginBottom: isMultiMode ? 8 : 0}}>
                  {isMultiMode
                    ? t("run.selectTopics")
                    : <>{terms?.length} {t("run.readySubBefore")}</>
                  }
                </div>

                {/* Topic checkboxen — nur im multi mode */}
                {isMultiMode && topicsForSelection.length > 0 && (
                  <>
                    <div style={{display:"flex", justifyContent:"center", gap:12, margin:"8px 0 4px"}}>
                      <button className="run-select-all" onClick={() =>
                        setSelectedTopicIds(topicsForSelection.map(tp => tp.id))
                      }>{t("run.allTopics")}</button>
                      <span style={{color:"#e2e8f0"}}>|</span>
                      <button className="run-select-all" style={{color:"#94a3b8"}} onClick={() =>
                        setSelectedTopicIds([])
                      }>None</button>
                    </div>
                    <div className="run-topic-list">
                      {topicsForSelection.map(tp => {
                        const checked = selectedTopicIds.includes(tp.id);
                        const termCount = (allTermsByTopic?.[tp.id] || []).length;
                        return (
                          <button
                            key={tp.id}
                            className={`run-topic-item ${checked ? "checked" : ""}`}
                            onClick={() => toggleTopic(tp.id)}
                          >
                            <span className="run-topic-check">{checked ? "✓" : ""}</span>
                            <span className="run-topic-name">{tp.name}</span>
                            <span className="run-topic-count">{termCount} terms</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : running ? (
              <div className="run-state">
                <div className="run-spinner-lg" />
                <div className="run-state-title">{t("run.runningTitle")}</div>
                <div className="run-state-sub">{t("run.runningSub")}</div>
              </div>
            ) : runResults ? (
              // Success message after run completes
              <div className="run-state">
                <div style={{fontSize: 48, marginBottom: 16}}>✅</div>
                <div className="run-state-title" style={{color: "#0f172a", fontSize: 16, fontWeight: 700, maxWidth: 480, textAlign: "center", lineHeight: 1.5, margin: "0 auto"}}>
                  {t("run.successTitle")}
                </div>
              </div>
            ) : null
          )}
        </div>

        {/* Footer */}
        <div className="run-footer">
          <div style={{fontSize:12.5, color:"#64748b"}}>
            {isDataMode && selectedRun && `${t("run.runFrom")} ${formatDate(selectedRun.runDate)}`}
            {isDataMode && evalDone && <span style={{marginLeft:8, color:"#16a34a"}}>✓ Auswertung gespeichert</span>}
            {!isDataMode && runResults && `${runResults.length} ${t("run.termsQueried")}${runSaved ? ` • ${t("run.saved")} ✓` : ""}`}
            {!isDataMode && !runResults && !running && isMultiMode && totalTermsSelected > 0 &&
              `${totalTermsSelected} ${t("run.searchTerms")} ${t("run.readySubBefore").split(".")[0]}`
            }
          </div>
          <div style={{display:"flex", gap:10}}>
            {isDataMode && selectedRun && (
              <button
                className="run-btn run-btn-ghost"
                onClick={handleEvaluate}
                disabled={evaluating}
                style={{display:"flex", alignItems:"center", gap:6}}
              >
                {evaluating
                  ? <><div className="run-spinner" /> Auswertung läuft…</>
                  : <>📊 Auswertung erstellen</>
                }
              </button>
            )}
            <button className="run-btn run-btn-ghost" onClick={onClose}>{t("run.close")}</button>
            {!isDataMode && (
              <button className="run-btn run-btn-primary" onClick={runResults ? onClose : handleRun} disabled={running || (isMultiMode && selectedTopicIds.length === 0)}>
                {running ? (
                  <><div className="run-spinner" /> {t("run.running")}</>
                ) : runResults ? (
                  t("run.toOverview")
                ) : (
                  <><svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg> {t("run.runQueries")}</>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
